import React, { useMemo, useState } from 'react';
import { Share2 } from 'lucide-react';
import { getCommunityRating, displayName, parseLocalDate } from '../utils/calculations';
import TeamFlag from './ui/TeamFlag';

// ─── Formações por número de titulares [GK, DEF, MID, ATK] ───────────────────
const FORMATION_SLOTS = {
  1:  [1, 0, 0, 0],
  2:  [1, 0, 1, 0],
  3:  [1, 1, 1, 0],
  4:  [1, 1, 1, 1],
  5:  [1, 2, 1, 1],
  6:  [1, 2, 2, 1],
  7:  [1, 2, 2, 2],
  8:  [1, 2, 3, 2],
  9:  [1, 2, 3, 3],
  10: [1, 3, 3, 3],
  11: [1, 4, 3, 3],
};
const POSITIONS = ['Goleiro', 'Defensor', 'Meia', 'Atacante'];

/**
 * Distribui titulares em 4 linhas de campo garantindo que nenhuma linha fique
 * vazia. Preenche cada linha com jogadores da posição natural primeiro;
 * os excedentes preenchem as linhas que ainda têm vagas.
 */
const buildRows = (starters) => {
  const n = starters.length;
  const slots = FORMATION_SLOTS[Math.max(1, Math.min(11, n))] || [1, 2, 2, 1];

  // Agrupa por posição natural (desconhecidos → Meia)
  const buckets = { Goleiro: [], Defensor: [], Meia: [], Atacante: [] };
  starters.forEach(p => {
    const preferredPosition = p.lineupPosition || p.position;
    const pos = buckets[preferredPosition] !== undefined ? preferredPosition : 'Meia';
    buckets[pos].push(p);
  });

  // 1ª passagem: preenche até o limite de cada linha
  const rows = POSITIONS.map((pos, i) => buckets[pos].slice(0, slots[i]));
  const overflow = POSITIONS.flatMap((pos, i) => buckets[pos].slice(slots[i]));

  // 2ª passagem: vagas restantes ← overflow
  let oi = 0;
  rows.forEach((row, i) => {
    const need = slots[i] - row.length;
    for (let j = 0; j < need && oi < overflow.length; j++, oi++) {
      row.push(overflow[oi]);
    }
  });

  return rows; // [gkRow, defRow, midRow, atkRow]
};

/* ─── PlayerCard estilo CartolaFC ─── */
const PlayerCard = ({ player, getRating, isCaptain, draggable = false, onDragStart, onClick }) => {
  const rating = getRating ? getRating(player) : getCommunityRating(player);
  const isGK = player.position === 'Goleiro';
  const photo = player.photo || '👤';
  const hasTeam = player.heartTeam && player.heartTeam !== 'Sem Time';

  return (
    <div className={`flex flex-col items-center ${draggable ? 'cursor-grab active:cursor-grabbing' : onClick ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`} style={{ width: 56 }} draggable={draggable} onDragStart={onDragStart} onClick={onClick}>

      {/* ── Balão da nota — acima da foto, estilo Cartola ── */}
      <div className="relative mb-0.5">
        <div className="bg-white text-slate-800 text-[9px] font-black px-2 py-[2px] rounded-full shadow-md border border-slate-200 min-w-[30px] text-center tabular-nums leading-tight whitespace-nowrap">
          ⭐ {rating != null ? rating.toFixed(1) : '–'}
        </div>
        {/* Ponteiro do balão */}
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-0 h-0"
          style={{ borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '5px solid white' }} />
      </div>

      {/* ── Foto + escudo + capitão ── */}
      <div className="relative mt-1 mb-1">
        {/* Braçadeira de capitão — canto superior esquerdo */}
        {isCaptain && (
          <div className="absolute -top-1 -left-1 z-20 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center shadow-md border-2 border-white">
            <span className="text-white font-black text-[8px] leading-none">C</span>
          </div>
        )}
        {player.photoURL ? (
          <div className={`w-12 h-12 rounded-full ring-2 shadow-lg overflow-hidden bg-slate-200 ${isGK ? 'ring-yellow-300' : 'ring-white'}`}>
            <img src={player.photoURL} alt="" className="w-full h-full object-cover" crossOrigin="anonymous" />
          </div>
        ) : (
          <div className={`w-12 h-12 rounded-full ring-2 shadow-lg flex items-center justify-center text-2xl ${isGK ? 'bg-yellow-400 ring-yellow-300' : 'bg-white ring-white'}`}>
            {photo}
          </div>
        )}
        {/* Escudo — canto inferior direito */}
        {hasTeam && (
          <div className="absolute -bottom-1 -right-2 z-10 drop-shadow-md">
            <TeamFlag team={player.heartTeam} size="sm" />
          </div>
        )}
      </div>

      {/* ── Nome ── */}
      <div className="bg-green-900 text-white text-[8px] px-1.5 py-0.5 rounded truncate w-full text-center font-semibold mt-1">
        {displayName(player.name, true) || '?'}
      </div>

    </div>
  );
};

/* ─── SoccerField ─── */
const SoccerField = ({ players, playersPerTeam, getRating, teamLabel, groupName, gameDate, gameTime, teams, confirmedIds, editable = false, onPositionChange, onPlayerClick }) => {
  const [draggedPlayerId, setDraggedPlayerId] = useState(null);


  /**
   * Ordena o elenco: GK sempre primeiro, depois pela ordem de confirmação de
   * presença (índice em confirmedIds). Avulsos/externos não têm índice → vão
   * para o final, ficando no banco quando há mais jogadores do que vagas.
   */
  const sorted = useMemo(() => {
    const order = confirmedIds || [];
    return [...players].sort((a, b) => {
      const aGK = a.position === 'Goleiro' ? 0 : 1;
      const bGK = b.position === 'Goleiro' ? 0 : 1;
      if (aGK !== bGK) return aGK - bGK;
      const ia = order.indexOf(a.id);
      const ib = order.indexOf(b.id);
      return (ia === -1 ? 9999 : ia) - (ib === -1 ? 9999 : ib);
    });
  }, [players, confirmedIds]);

  const starters = sorted.slice(0, playersPerTeam);
  const bench    = sorted.slice(playersPerTeam);

  /* Capitão = titular com maior nota */
  const captainId = starters.length
    ? starters.reduce((best, p) => {
        const r    = getRating ? getRating(p)    : getCommunityRating(p);
        const bestR = getRating ? getRating(best) : getCommunityRating(best);
        return r > bestR ? p : best;
      }).id
    : null;

  /* Distribui titulares nas linhas da formação */
  const rows = buildRows(starters);

  const shareText = useMemo(() => {
    const formatMatchDate = (value) => {
      if (!value) return '';
      return parseLocalDate(value).toLocaleDateString('pt-BR');
    };

    const sortTeam = (teamPlayers = []) => {
      const order = confirmedIds || [];
      return [...teamPlayers].sort((a, b) => {
        const aGK = a.position === 'Goleiro' ? 0 : 1;
        const bGK = b.position === 'Goleiro' ? 0 : 1;
        if (aGK !== bGK) return aGK - bGK;
        const ia = order.indexOf(a.id);
        const ib = order.indexOf(b.id);
        return (ia === -1 ? 9999 : ia) - (ib === -1 ? 9999 : ib);
      });
    };

    const buildTeamLines = (label, teamPlayers = []) => {
      const sortedTeam = sortTeam(teamPlayers);
      const startersTeam = sortedTeam.slice(0, playersPerTeam);
      const captain = startersTeam.length
        ? startersTeam.reduce((best, player) => {
            const rating = getRating ? getRating(player) : getCommunityRating(player);
            const bestRating = getRating ? getRating(best) : getCommunityRating(best);
            return rating > bestRating ? player : best;
          })
        : null;

      const lines = [label, ''];
      sortedTeam.forEach((player) => {
        const name = displayName(player.name) || 'Jogador';
        const captainTag = captain?.id === player.id ? ' [C]' : '';
        const goalkeeperTag = player.position === 'Goleiro' ? ' (Goleiro)' : '';
        lines.push(`${name}${captainTag}${goalkeeperTag}`);
      });

      return lines;
    };

    const header = [
      [groupName, formatMatchDate(gameDate), gameTime].filter(Boolean).join(' - '),
      'Escalacoes:',
      '',
    ];

    const teamALines = buildTeamLines('Time A', teams?.teamA || []);
    const teamBLines = buildTeamLines('Time B', teams?.teamB || []);

    return [...header, ...teamALines, '', ...teamBLines].join('\n').trim();
  }, [confirmedIds, gameDate, gameTime, getRating, groupName, playersPerTeam, teams]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Escalacao - ${groupName || 'Pelada Match'}`,
          text: shareText,
        });
        return;
      }

      await navigator.clipboard.writeText(shareText);
    } catch {
      try {
        await navigator.clipboard.writeText(shareText);
      } catch {
        // share/copy cancelled
      }
    }
  };

  return (
    <div className="select-none mt-4">
      <div className="bg-slate-50 rounded-xl overflow-hidden">
      {/* Campo — aspect-ratio 3/4 com conteúdo absoluto (mesmo tamanho de antes) */}
      <div
        className="relative w-full rounded-xl overflow-hidden shadow-inner border-2 border-emerald-700"
        style={{
          paddingBottom: '133.33%', /* 4/3 = mesmo que aspect-[3/4] */
          background: 'linear-gradient(180deg, #16a34a 0%, #15803d 48%, #16a34a 100%)',
        }}
      >
        {/* Marcações SVG */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 300 400" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          {[0,1,2,3,4,5,6,7].map(i => (
            <rect key={i} x="0" y={i*50} width="300" height="50" fill={i%2===0 ? 'rgba(0,0,0,0.04)' : 'transparent'} />
          ))}
          <line x1="0" y1="200" x2="300" y2="200" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
          <circle cx="150" cy="200" r="38" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
          <circle cx="150" cy="200" r="2.5" fill="rgba(255,255,255,0.4)" />
          <rect x="80" y="0" width="140" height="56" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1.5" />
          <rect x="112" y="0" width="76" height="24" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1.5" />
          <rect x="80" y="344" width="140" height="56" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1.5" />
          <rect x="112" y="376" width="76" height="24" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1.5" />
        </svg>

        {/* Conteúdo — posição absoluta, ocupa todo o campo */}
        <div className="absolute inset-0 flex flex-col">
          {/* Header: label + share */}
          <div className="flex items-center justify-between px-4 pt-3 pb-1 shrink-0">
            <span className="text-white font-black text-sm tracking-wide drop-shadow">{teamLabel || 'Time'}</span>
            <button
              onClick={handleShare}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-1.5 rounded-full transition-colors"
              title="Compartilhar escalação em texto"
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Jogadores — linhas da formação (vazias são omitidas) */}
          <div className="flex-1 flex flex-col justify-around py-2">
            {rows.map((row, i) => {
              if (!editable && row.length === 0) return null;
              const position = POSITIONS[i];
              return (
                <div
                  key={position}
                  className={`flex justify-around items-end px-1 min-h-[54px] rounded-lg transition-colors ${editable && draggedPlayerId ? 'bg-white/10 ring-1 ring-white/30' : ''}`}
                  onDragOver={editable ? event => event.preventDefault() : undefined}
                  onDrop={editable ? event => { event.preventDefault(); const playerId = draggedPlayerId || event.dataTransfer.getData('text/plain'); if (playerId) onPositionChange?.(playerId, position); setDraggedPlayerId(null); } : undefined}
                >
                  {row.map((p, j) => (
                    <PlayerCard key={p.id || j} player={p} getRating={getRating} isCaptain={p.id === captainId} draggable={editable} onClick={!editable && !p.isGuest ? () => onPlayerClick?.(p) : undefined} onDragStart={event => { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', p.id); setDraggedPlayerId(p.id); }} />
                  ))}
                  {editable && row.length === 0 && <span className="self-center text-[9px] font-bold text-white/70 uppercase">{position}</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {editable && <p className="px-3 py-2 text-[11px] text-slate-500 text-center bg-slate-50">Arraste um jogador para a faixa desejada no campo.</p>}

      {/* Banco — sempre visível */}
      <div className="mt-0 bg-slate-100 p-3 border-t border-slate-200">
        <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 text-center">Banco de Reservas</div>
        {bench.length > 0 ? (
          <div className="flex flex-wrap justify-center gap-3">
            {bench.map((p, i) => <PlayerCard key={p.id || i} player={p} getRating={getRating} />)}
          </div>
        ) : (
          <p className="text-[11px] text-slate-400 text-center py-1">Sem reservas</p>
        )}
      </div>
      </div>
    </div>
  );
};

export default SoccerField;




